import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CreditCard, CheckCircle, AlertCircle, Loader2, Coffee, ArrowLeft, Sparkles, ArrowRight, Share2, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/utils/apiClient';

// Test card numbers that will "succeed"
const TEST_CARDS = ['4111111111111111', '4242424242424242', '5555555555554444'];

// Social share configuration
const SITE_URL = 'https://bulaccinocafe.com';
const SHARE_TEXT = "Ordering on the BulaccinoCafe.com website was a seamless experience. I highly recommend trying it for yourself!";
const SHARE_HASHTAGS = "BulaccinoCafe,FijiCoffee,CoffeeLovers";

// Facebook Icon SVG
const FacebookIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
  </svg>
);

// Instagram Icon SVG
const InstagramIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
  </svg>
);

// X (Twitter) Icon SVG
const XIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// WhatsApp Icon SVG
const WhatsAppIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const PaymentPage = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending'); // pending, processing, success, failed
  
  // Payment form state
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardName, setCardName] = useState('');

  useEffect(() => {
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  const fetchOrder = async () => {
    try {
      const response = await api.get(`/orders/${orderNumber}`);
      setOrder(response.data);
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
      }
    } catch (err) {
      setError('Order not found');
    } finally {
      setLoading(false);
    }
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  const formatExpiry = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPaymentStatus('processing');

    // Simulate payment processing delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Check if card is a test card
    const cleanCardNumber = cardNumber.replace(/\s/g, '');
    const isTestCard = TEST_CARDS.includes(cleanCardNumber);

    if (isTestCard) {
      // Simulate successful payment
      try {
        // Update order status to paid (test mode)
        await api.put(`/orders/${orderNumber}/status`, { 
          status: 'confirmed', 
          payment_status: 'paid',
          payment_method: 'card_test',
          paid_at: new Date().toISOString()
        });
      } catch (err) {
        console.log('Could not update order status');
      }
      setPaymentStatus('success');
    } else {
      // For non-test cards, show that payment gateway is not configured
      setError('Payment gateway (Windcave) is not yet configured. Use test card 4111 1111 1111 1111 to simulate a payment.');
      setPaymentStatus('failed');
    }
  };

  // Social share handlers with pre-populated text
  const handleFacebookShare = () => {
    // Facebook Feed Dialog with pre-populated quote
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SITE_URL)}&quote=${encodeURIComponent(SHARE_TEXT)}`;
    window.open(url, 'facebook-share', 'width=600,height=500,scrollbars=yes');
  };

  const handleXShare = () => {
    // X/Twitter Intent with pre-populated text
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(SITE_URL)}&hashtags=${SHARE_HASHTAGS}`;
    window.open(url, 'x-share', 'width=600,height=500,scrollbars=yes');
  };

  const handleWhatsAppShare = () => {
    // WhatsApp share with pre-populated message
    const message = `${SHARE_TEXT}\n\n${SITE_URL}`;
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleInstagramShare = () => {
    // Instagram doesn't have a direct share URL API
    // Copy the share text to clipboard and notify user
    const message = `${SHARE_TEXT}\n\n${SITE_URL}`;
    navigator.clipboard.writeText(message).then(() => {
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-semibold">Post text copied!</span>
          <span className="text-sm opacity-80">Paste it into your Instagram post or story</span>
        </div>,
        { duration: 4000 }
      );
      // Open Instagram after a brief delay
      setTimeout(() => {
        window.open('https://www.instagram.com/', '_blank');
      }, 500);
    }).catch(() => {
      toast.info('Opening Instagram - copy this text manually: ' + SHARE_TEXT);
      window.open('https://www.instagram.com/', '_blank');
    });
  };

  // Copy link handler
  const [linkCopied, setLinkCopied] = useState(false);
  const handleCopyLink = () => {
    navigator.clipboard.writeText(SITE_URL).then(() => {
      setLinkCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center" data-testid="payment-loading">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-gray-600 dark:text-gray-300">Loading order...</p>
        </div>
      </div>
    );
  }

  if (error && !order) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4" data-testid="payment-error">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Order Not Found</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="gap-2" data-testid="back-home-btn">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  // Success state - with Coffee Pass promo and social share
  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-green-900/20 dark:to-gray-900 py-8 px-4" data-testid="payment-success">
        <div className="max-w-md mx-auto space-y-6">
          {/* Success Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Payment Successful!</h1>
            <p className="text-gray-600 dark:text-gray-300 mb-6">Thank you for your order</p>
            
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 mb-6 text-left">
              <div className="flex items-center gap-3 mb-4">
                <Coffee className="w-8 h-8 text-primary" />
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Order #{order?.order_number}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Ready for pickup in ~10-15 mins</p>
                </div>
              </div>
              
              <div className="border-t border-gray-200 dark:border-gray-600 pt-4 mt-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Order Items:</h3>
                {order?.items?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm py-1">
                    <span className="text-gray-700 dark:text-gray-300">{item.quantity}x {item.name} {item.size && `(${item.size})`}</span>
                    <span className="text-gray-600 dark:text-gray-400">{(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <div className="border-t border-gray-200 dark:border-gray-600 mt-3 pt-3 flex justify-between font-semibold">
                  <span className="text-gray-900 dark:text-white">Total Paid</span>
                  <span className="text-green-600 dark:text-green-400">{order?.total?.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Please show this screen when you collect your order at Bulaccino Cafe
            </p>
          </div>

          {/* Bula Coffee Pass Promotion Card - PROMINENT */}
          <div className="bg-gradient-to-br from-primary to-amber-600 rounded-2xl shadow-lg p-6 text-white" data-testid="coffee-pass-promo">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5" />
              <span className="text-sm font-semibold uppercase tracking-wide opacity-90">Exclusive Offer</span>
            </div>
            
            <h2 className="text-2xl font-bold mb-2">Love Our Coffee?</h2>
            <p className="text-white/90 mb-4">
              Get <strong>1 FREE coffee every single day</strong> for a whole year with Bula Coffee Pass!
            </p>
            
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold">40</p>
                  <p className="text-sm opacity-90">per year</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold">365 FREE Coffees</p>
                  <p className="text-sm opacity-90">1,825 value!</p>
                </div>
              </div>
            </div>
            
            <Link to="/bula-coffee-club" className="block">
              <Button 
                size="lg" 
                className="w-full bg-white text-primary hover:bg-white/90 rounded-full font-semibold"
                data-testid="join-coffee-pass-btn"
              >
                <Coffee className="w-5 h-5 mr-2" />
                Join Bula Coffee Pass
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {/* Social Share Section */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6" data-testid="social-share-section">
            <div className="flex items-center gap-2 mb-3">
              <Share2 className="w-5 h-5 text-primary" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Share Your Experience</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">
              Loved your order? Tell your friends about Bulaccino Cafe!
            </p>
            
            {/* Primary share buttons - 2x2 grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={handleFacebookShare}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-[#1877F2] hover:bg-[#166FE5] active:bg-[#1466D8] text-white rounded-xl font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                data-testid="share-facebook-btn"
              >
                <FacebookIcon />
                <span>Facebook</span>
              </button>
              <button
                onClick={handleXShare}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-black dark:bg-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-100 active:bg-gray-700 dark:active:bg-gray-200 text-white rounded-xl font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                data-testid="share-x-btn"
              >
                <XIcon />
                <span>Post on X</span>
              </button>
              <button
                onClick={handleWhatsAppShare}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] hover:bg-[#20BD5A] active:bg-[#1DA851] text-white rounded-xl font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                data-testid="share-whatsapp-btn"
              >
                <WhatsAppIcon />
                <span>WhatsApp</span>
              </button>
              <button
                onClick={handleInstagramShare}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 active:opacity-80 text-white rounded-xl font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                data-testid="share-instagram-btn"
              >
                <InstagramIcon />
                <span>Instagram</span>
              </button>
            </div>

            {/* Copy link section */}
            <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
              <input
                type="text"
                value={SITE_URL}
                readOnly
                className="flex-1 bg-transparent text-sm text-gray-700 dark:text-gray-200 outline-none truncate"
              />
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary hover:text-primary/80 dark:text-primary dark:hover:text-primary/80 transition-colors"
                data-testid="copy-link-btn"
              >
                {linkCopied ? (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Order More Button - Secondary action */}
          <Button 
            onClick={() => navigate('/menu')} 
            variant="outline" 
            className="w-full bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700" 
            data-testid="order-more-btn"
          >
            Order More
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4" data-testid="payment-page">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Coffee className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Bulaccino Cafe</h1>
          <p className="text-gray-600 dark:text-gray-300">Complete your payment</p>
        </div>

        {/* Order Summary Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 mb-6" data-testid="order-summary">
          <h2 className="font-semibold text-lg text-gray-900 dark:text-white mb-4">Order #{order?.order_number}</h2>
          
          <div className="space-y-2 mb-4">
            {order?.items?.map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-gray-700 dark:text-gray-300">
                  {item.quantity}x {item.name}
                  {item.size && <span className="text-gray-500 dark:text-gray-400"> ({item.size})</span>}
                </span>
                <span className="text-gray-900 dark:text-white">{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>

          {order?.discount > 0 && (
            <div className="flex justify-between text-sm text-green-600 dark:text-green-400 border-t border-gray-100 dark:border-gray-700 pt-2">
              <span>Loyalty Discount</span>
              <span>-{order.discount.toFixed(2)}</span>
            </div>
          )}

          <div className="flex justify-between font-semibold text-lg border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
            <span className="text-gray-900 dark:text-white">Total</span>
            <span className="text-primary">{order?.total?.toFixed(2)}</span>
          </div>
        </div>

        {/* Payment Form */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6" data-testid="payment-form-container">
          <div className="flex items-center gap-2 mb-6">
            <CreditCard className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h2 className="font-semibold text-lg text-gray-900 dark:text-white">Payment Details</h2>
          </div>

          {/* Test Mode Notice */}
          <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Test Mode:</strong> Use card number <code className="bg-amber-100 dark:bg-amber-800 px-1 rounded">4111 1111 1111 1111</code> with any expiry and CVV to simulate payment.
            </p>
          </div>

          {error && paymentStatus === 'failed' && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-4 mb-6" data-testid="payment-error-message">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="payment-form">
            <div>
              <Label htmlFor="cardName" className="text-gray-700 dark:text-gray-300">Name on Card</Label>
              <Input
                id="cardName"
                type="text"
                placeholder="John Smith"
                value={cardName}
                onChange={(e) => setCardName(e.target.value)}
                required
                className="mt-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
                data-testid="card-name-input"
              />
            </div>

            <div>
              <Label htmlFor="cardNumber" className="text-gray-700 dark:text-gray-300">Card Number</Label>
              <Input
                id="cardNumber"
                type="text"
                placeholder="4111 1111 1111 1111"
                value={cardNumber}
                onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                maxLength={19}
                required
                className="mt-1 font-mono bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
                data-testid="card-number-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="expiry" className="text-gray-700 dark:text-gray-300">Expiry Date</Label>
                <Input
                  id="expiry"
                  type="text"
                  placeholder="MM/YY"
                  value={expiry}
                  onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                  maxLength={5}
                  required
                  className="mt-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  data-testid="card-expiry-input"
                />
              </div>
              <div>
                <Label htmlFor="cvv" className="text-gray-700 dark:text-gray-300">CVV</Label>
                <Input
                  id="cvv"
                  type="text"
                  placeholder="123"
                  value={cvv}
                  onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  maxLength={4}
                  required
                  className="mt-1 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  data-testid="card-cvv-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full mt-6 h-12 text-base"
              disabled={paymentStatus === 'processing'}
              data-testid="pay-button"
            >
              {paymentStatus === 'processing' ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>Pay {order?.total?.toFixed(2)}</>
              )}
            </Button>
          </form>

          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
            Your payment is secure and encrypted
          </p>
        </div>

        {/* Back Link */}
        <div className="text-center mt-6">
          <button 
            onClick={() => navigate(-1)} 
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            data-testid="back-link"
          >
            ← Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
